'use client';

import { flex, text, vstack } from '@onbeam/business-styled-system/patterns';
import { useFormContext } from '../../Form/FormContext';
import { FormSteps } from '../../Form/FormSteps';
import { BountyFormSchema, bountyFormSteps } from './BountyForm.utils';
import { BountyFormProps } from './BountyForm.types';
import { DatePicker, Input, Radio, Textarea, toast } from '@onbeam/business-ui';
import {
  ApplicationTeamType,
  applicationTeamTypeMap,
} from '../../Applications/ApplicationsList/ApplicationsList.types';
import { BountyFormReview } from './BountyFormReview';
import { Controller } from 'react-hook-form';
import { FormGroup } from '../../Form/FormGroup';
import { css } from '@onbeam/business-styled-system/css';
import { submitBounty } from '@/actions/submitBounty';
import { useRouter } from 'next/navigation';

export const BountyFormContent = ({ bounty }: BountyFormProps) => {
  const router = useRouter();
  const {
    onContinue,
    onPrevious,
    onNavigate,
    activeIndex,
    isReviewing,
    form: {
      handleSubmit,
      register,
      control,
      formState: { errors },
    },
  } = useFormContext<BountyFormSchema>();

  const formSteps = bountyFormSteps(bounty);
  const activeStep = formSteps[activeIndex];

  const onSubmit = async (values: BountyFormSchema) => {
    await submitBounty(values)
      .then(() => {
        toast.success('Thank you for your submission', {
          description: 'We’ll review it and get back to you',
        });
        router.push('/');
      })
      .catch((error) => {
        console.error('Error submitting bounty:', error);
        toast.error('There was an error submitting the form.', {
          description: 'Please verify your information and try again.',
        });
      });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div
        className={vstack({
          gap: '10',
          mt: '[3.75rem]',
          w: 'full',

          md: {
            mt: '10',
            maxW: '[26.25rem]',
            mx: 'auto',
          },
        })}
      >
        {isReviewing ? (
          <BountyFormReview />
        ) : (
          <>
            <div className={vstack({ gap: '4' })}>
              <h1
                className={text({
                  style: 'subtitle-md',
                })}
              >
                <span>{activeStep.title}</span>
                {activeStep.subtitle && (
                  <span className={text({ color: 'mono.tertiary' })}>
                    {' '}
                    {activeStep.subtitle}
                  </span>
                )}
              </h1>
              {activeStep.description && (
                <p
                  className={text({
                    style: 'body-md',
                    color: 'mono.tertiary',
                  })}
                >
                  {activeStep.description}
                </p>
              )}
            </div>
            <div className={vstack({ gap: '8', pb: '40' })}>
              {activeStep.fieldNames.map((fieldName) => {
                const defaultProps = {
                  errorMessage: errors[fieldName]?.message,
                  ...register(fieldName),
                };
                switch (fieldName) {
                  case 'idea':
                    return (
                      <Textarea
                        key={fieldName}
                        label="How will you complete this bounty?"
                        placeholder="Share your idea, approach, and why it’s a good fit for Beam."
                        inputClassName={flex({ h: '[25rem]' })}
                        {...defaultProps}
                      />
                    );
                  case 'teamType':
                    return (
                      <FormGroup
                        key={fieldName}
                        label="Who’s working on this?"
                        errorMessage={defaultProps.errorMessage}
                        className={css({ mb: '-2' })}
                      >
                        <div className={flex({ wrap: 'wrap', gap: '2' })}>
                          {Object.values(ApplicationTeamType).map(
                            (teamType) => (
                              <Radio
                                {...register('teamType')}
                                variant="chip"
                                value={teamType}
                                key={teamType}
                              >
                                {applicationTeamTypeMap[teamType]}
                              </Radio>
                            ),
                          )}
                        </div>
                      </FormGroup>
                    );
                  case 'teamDescription':
                    return (
                      <Textarea
                        key={fieldName}
                        placeholder="List your team members, roles, and a bit about your experience."
                        inputClassName={flex({ h: '[17.5rem]' })}
                        {...defaultProps}
                      />
                    );
                  case 'email':
                    return (
                      <Input
                        key={fieldName}
                        type="email"
                        label="Your email"
                        placeholder="Enter email address"
                        {...defaultProps}
                      />
                    );
                  case 'estimatedCost':
                    return (
                      <Textarea
                        key={fieldName}
                        label="Estimated cost"
                        placeholder="Rough budget"
                        inputClassName={flex({ h: '[17.5rem]' })}
                        {...defaultProps}
                      />
                    );
                  case 'dateRange':
                    return (
                      <Controller
                        key={fieldName}
                        control={control}
                        name="dateRange"
                        render={({
                          field: { onChange, onBlur: _, ...field },
                          fieldState: { error },
                        }) => (
                          <FormGroup errorMessage={error?.message}>
                            <DatePicker
                              mode="range"
                              label="When will you start & finish?"
                              placeholder="Expected start and completion dates."
                              onValueChange={onChange}
                              {...field}
                            />
                          </FormGroup>
                        )}
                      />
                    );
                  case 'demoURL':
                    return (
                      <Input
                        key={fieldName}
                        type="url"
                        label="Link to demo, MVP, or repo"
                        placeholder="Have something to show? Paste the link here."
                        {...defaultProps}
                      />
                    );
                  default:
                    return null;
                }
              })}
            </div>
          </>
        )}
      </div>
      {!isReviewing && (
        <FormSteps
          steps={formSteps}
          activeStep={activeIndex}
          onContinue={onContinue}
          onPrevious={onPrevious}
          onNavigate={onNavigate}
        />
      )}
    </form>
  );
};
